/**
 * File-share upload items only a Restart can fix (plan §3.3 fail-closed Resume; S7 review D F2), shared by main and the
 * transfers UI. Such an item keeps its attempt — a lost lease, an interrupted overwrite, an unrecorded or changed staged
 * copy, a local file that changed — so neither Resume nor a per-item Retry may continue it: Retry is refused in main and
 * shown unavailable (with this reason) in the transfers drawer, which offers Restart instead. Process-agnostic.
 */
import type { TransferJob } from '../../types/azureStorageTransfers';
import { storageServiceOf } from '../../types/azureStorageResource';

/** Answers of a write with our staging lease that mean the lease is gone (Task 7.0 I8 adds LeaseLostWithFileOperation). */
export const UPLOAD_LEASE_LOST_CODES: ReadonlySet<string> = new Set(['LeaseLostWithFileOperation', 'LeaseIdMismatchWithFileOperation', 'LeaseLost', 'LeaseIdMissing', 'LeaseNotPresentWithFileOperation']);
/** An item failed with one of these can only start over (`itemResumable` false → the job is restart-only). */
export const RESTART_ONLY_UPLOAD_CODES: ReadonlySet<string> = new Set([...UPLOAD_LEASE_LOST_CODES, 'OverwriteInterrupted', 'IdentityNotRecorded', 'StagingNotResumable', 'UploadSourceChanged']);

export const RETRY_NEEDS_RESTART_REASON = 'This file cannot be retried on its own: its upload has to start over. Use Restart to upload it again.';

/** A file-share upload job's item error that only Restart can fix (blob and other jobs: never). */
export function isRestartOnlyUploadError(job: Pick<TransferJob, 'kind' | 'destination'>, code: string | undefined): boolean {
  if (job.kind !== 'upload' || job.destination.kind !== 'remote' || !code) return false;
  return storageServiceOf(job.destination.resource) === 'file' && RESTART_ONLY_UPLOAD_CODES.has(code);
}
